import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SERVICES_LIST, SOLUTION_CATEGORIES, SUPPORTED_INDIAN_BANKS, OFFICIAL_LINKS } from '../data/servicesData';
import { CreditCard, Send, Smartphone, Landmark, Coins, ShieldCheck, FileCheck, Rocket, ArrowRight, Sparkles, Eye, ChevronDown, Calculator, Zap, Terminal, MessageCircle } from 'lucide-react';

export const SolutionsExplorer = ({
  onSelectProduct,
  onOpenInquiry,
}) => {
  const [selectedCategory, setSelectedCategory] = useState('pay-in');

  // Loan & EMI Calculator State
  const [loanType, setLoanType] = useState('business');
  const [loanAmount, setLoanAmount] = useState(2000000);
  const [interestRate, setInterestRate] = useState(12.5);
  const [tenureYears, setTenureYears] = useState(4);

  const applyLoanPreset = (type) => {
    setLoanType(type);
    if (type === 'personal') {
      setLoanAmount(500000);
      setInterestRate(11.5);
      setTenureYears(3);
    } else if (type === 'business') {
      setLoanAmount(2000000);
      setInterestRate(12.5);
      setTenureYears(4);
    } else if (type === 'home') {
      setLoanAmount(5000000);
      setInterestRate(8.5);
      setTenureYears(15);
    } else {
      setLoanAmount(1500000);
      setInterestRate(12.0);
      setTenureYears(5);
    }
  };

  const monthlyRate = interestRate / 12 / 100;
  const totalMonths = tenureYears * 12;
  const emi = Math.round(
    (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) /
    (Math.pow(1 + monthlyRate, totalMonths) - 1)
  );
  const totalPayment = emi * totalMonths;
  const totalInterest = totalPayment - loanAmount;
  const partnerCommission = Math.round(loanAmount * 0.022);

  const currentProducts = SERVICES_LIST.filter(s => s.category === selectedCategory);

  const getCategoryIcon = (iconName, isGreen) => {
    const cls = `w-4 h-4 ${isGreen ? 'text-emerald-600' : 'text-blue-700'}`;
    switch (iconName) {
      case 'CreditCard': return <CreditCard className={cls} />;
      case 'Send': return <Send className={cls} />;
      case 'FileCheck': return <FileCheck className={cls} />;
      case 'Terminal': return <Terminal className={cls} />;
      case 'Smartphone': return <Smartphone className={cls} />;
      case 'Landmark': return <Landmark className={cls} />;
      case 'Coins': return <Coins className={cls} />;
      case 'ShieldCheck': return <ShieldCheck className={cls} />;
      case 'Rocket': return <Rocket className={cls} />;
      default: return <CreditCard className={cls} />;
    }
  };

  return (
    <section id="solutions-section" className="py-14 bg-slate-50/70 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-9">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-100/70 text-blue-900 text-xs font-black uppercase tracking-wider border border-blue-200">
            <Sparkles className="w-3.5 h-3.5 text-blue-700" />
            <span>All Solutions &amp; Infrastructure</span>
          </div>
          <h2 className="text-2xl sm:text-4xl font-black text-[#0c2e6b] tracking-tight">
            Explore All Solutions &amp; Services
          </h2>
          <p className="text-xs sm:text-sm text-slate-600">
            We provide all the solutions for your business. Select a category below or click <strong className="text-blue-700 font-semibold">View More</strong> on any product to see full deliverables and send your submit request.
          </p>
        </div>

        {/* Dropdown Selector for Mobile */}
        <div className="max-w-md mx-auto sm:hidden">
          <label className="block text-xs font-bold text-slate-700 mb-1.5">
            Choose Solution Type:
          </label>
          <div className="relative">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full appearance-none px-4 py-3 bg-white rounded-2xl border-2 border-blue-300 text-xs font-bold text-blue-950 focus:outline-none focus:border-blue-600 shadow-xs"
            >
              {SOLUTION_CATEGORIES.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.label} {cat.badge ? `(${cat.badge})` : ''}
                </option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 text-blue-700 absolute right-4 top-3.5 pointer-events-none" />
          </div>
        </div>

        {/* Animated Horizontal Shifting Pills Bar for Desktop */}
        <div className="hidden sm:flex items-center justify-center gap-2 flex-wrap">
          {SOLUTION_CATEGORIES.map((cat) => {
            const isActive = selectedCategory === cat.id;
            return (
              <motion.button
                key={cat.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelectedCategory(cat.id)}
                className={`relative px-4 py-2.5 rounded-2xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                  isActive
                    ? cat.isGreen
                      ? 'bg-emerald-700 text-white shadow-md shadow-emerald-700/20'
                      : 'bg-[#0c2e6b] text-white shadow-md shadow-blue-900/20'
                    : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                <span>{getCategoryIcon(cat.icon, isActive ? false : cat.isGreen)}</span>
                <span>{cat.label}</span>
                {cat.badge && (
                  <span className={`text-[10px] px-1.5 py-px rounded-full font-black uppercase tracking-wider ${
                    isActive 
                      ? 'bg-white/20 text-white' 
                      : cat.isGreen 
                        ? 'bg-emerald-100 text-emerald-800' 
                        : 'bg-blue-100 text-blue-800'
                  }`}>
                    {cat.badge}
                  </span>
                )}
                {isActive && (
                  <motion.div
                    layoutId="active-solution-pill"
                    className="absolute inset-0 rounded-2xl border-2 border-white/30 pointer-events-none"
                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                  />
                )}
              </motion.button>
            );
          })}
        </div>

        {/* Highlight Banner: Instant Payout Services & All Banks */}
        {selectedCategory === 'payout' && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-5 rounded-3xl bg-gradient-to-r from-blue-950 via-blue-900 to-indigo-950 text-white shadow-lg border-2 border-blue-400/40 space-y-4"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-blue-800/80 pb-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping"></span>
                  <span className="text-xs font-black uppercase tracking-wider text-emerald-300">
                    Instant Payout Rails: T+0 &amp; T+1 Disbursals Live
                  </span>
                </div>
                <p className="text-xs text-blue-100 mt-1">
                  24x7x365 instant merchant-to-customer and vendor bank account transfers via IMPS, NEFT, RTGS, and UPI.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => onOpenInquiry('Instant Payouts & All Banks Setup')}
                  className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black text-xs uppercase tracking-wider shrink-0 transition-colors shadow-sm cursor-pointer flex items-center gap-1.5"
                >
                  <span>Submit Request</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>

                <a
                  href={`https://wa.me/91${OFFICIAL_LINKS.phone}?text=${encodeURIComponent('Hello Fi Pay Bills, I need Instant Payouts (T+0/T+1) setup across all Indian banks for my business.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3.5 py-2 rounded-xl bg-white/15 hover:bg-white/25 text-white border border-white/20 font-bold text-xs uppercase tracking-wider shrink-0 transition-colors flex items-center gap-1.5"
                >
                  <MessageCircle className="w-3.5 h-3.5 text-emerald-400" />
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>

            {/* All Banks Grid */}
            <div>
              <span className="text-[11px] font-black uppercase tracking-wider text-blue-200 block mb-2">
                All Major Indian Banks Supported for Instant Payouts:
              </span>
              <div className="flex flex-wrap gap-1.5">
                {SUPPORTED_INDIAN_BANKS.map((bank, idx) => (
                  <span 
                    key={idx}
                    className="px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/15 text-xs text-white border border-white/15 font-semibold transition-colors"
                  >
                    {bank}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Highlight Banner: Validation Kit (PAN, Aadhaar, Bank Details) */}
        {selectedCategory === 'validation' && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-5 rounded-3xl bg-gradient-to-r from-emerald-950 via-teal-900 to-slate-900 text-white shadow-lg border-2 border-emerald-400/40 space-y-4"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-teal-800/80 pb-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  <span className="text-xs font-black uppercase tracking-wider text-emerald-300">
                    Comprehensive Validation Kit Suite (All APIs Active)
                  </span>
                </div>
                <p className="text-xs text-teal-100 mt-1">
                  Single developer API key for PAN, Aadhaar OTP/QR, ₹1 Bank Penny Drop, UPI VPA, and GSTIN verification.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => onOpenInquiry('Validation Kit API Suite Integration')}
                  className="px-4 py-2 rounded-xl bg-white hover:bg-emerald-50 text-emerald-950 font-black text-xs uppercase tracking-wider shrink-0 transition-colors shadow-sm cursor-pointer flex items-center gap-1.5"
                >
                  <span>Submit Request</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>

                <a
                  href={`https://wa.me/91${OFFICIAL_LINKS.phone}?text=${encodeURIComponent('Hello Fi Pay Bills, I need the complete Validation Kit API (PAN, Aadhaar, Bank Penny Drop, UPI VPA) for my platform.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3.5 py-2 rounded-xl bg-white/15 hover:bg-white/25 text-white border border-white/20 font-bold text-xs uppercase tracking-wider shrink-0 transition-colors flex items-center gap-1.5"
                >
                  <MessageCircle className="w-3.5 h-3.5 text-emerald-300" />
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div className="p-2.5 rounded-xl bg-white/10 border border-white/15">
                <span className="font-bold text-emerald-300 block">PAN Verification</span>
                <span className="text-[11px] text-teal-100">Status, Name &amp; Aadhaar Seed</span>
              </div>
              <div className="p-2.5 rounded-xl bg-white/10 border border-white/15">
                <span className="font-bold text-emerald-300 block">Aadhaar &amp; e-KYC</span>
                <span className="text-[11px] text-teal-100">UIDAI Paperless &amp; Biometric</span>
              </div>
              <div className="p-2.5 rounded-xl bg-white/10 border border-white/15">
                <span className="font-bold text-emerald-300 block">Bank Penny Drop</span>
                <span className="text-[11px] text-teal-100">₹1 Name Match across All Banks</span>
              </div>
              <div className="p-2.5 rounded-xl bg-white/10 border border-white/15">
                <span className="font-bold text-emerald-300 block">UPI VPA &amp; GSTIN</span>
                <span className="text-[11px] text-teal-100">NPCI Handle &amp; GST Return Status</span>
              </div>
            </div>
          </motion.div>
        )}

        {/* Highlight Banner for API Services */}
        {selectedCategory === 'api-services' && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 rounded-2xl bg-emerald-900 text-white shadow-md border border-emerald-700/50 flex flex-col md:flex-row items-center justify-between gap-4"
          >
            <div className="space-y-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
                <span className="font-bold text-xs uppercase tracking-wider text-emerald-200">
                  Full API Services Stack (14+ APIs)
                </span>
              </div>
              <p className="text-xs text-slate-200">
                PAYOUT &bull; AEPS &bull; MATM &bull; DMT &bull; CMS &bull; RECHARGE &bull; TRAVEL &bull; ALL VALIDATION APIS
              </p>
            </div>
            <button
              onClick={() => onOpenInquiry('Custom POST APIs & Integration Request')}
              className="px-4 py-2.5 rounded-xl bg-white text-emerald-950 font-bold text-xs shrink-0 hover:bg-emerald-50 transition-colors shadow-sm cursor-pointer"
            >
              Submit Request
            </button>
          </motion.div>
        )}

        {/* HIGHLIGHTED LOAN AND EMI CALCULATOR SECTION */}
        {selectedCategory === 'loans' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.25 }}
            className="p-6 sm:p-8 rounded-3xl bg-white border-2 border-blue-400/80 shadow-xl shadow-blue-900/10 space-y-6"
          >
            {/* Header with High-Contrast Badge */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-5 border-b border-slate-200">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 rounded-full bg-blue-700 text-white text-[11px] font-black uppercase tracking-wider">
                    Highlighted Tool
                  </span>
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md">
                    Multi-Lender Platform Live
                  </span>
                </div>
                <h3 className="text-lg sm:text-xl font-black text-[#0c2e6b] flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-blue-700" />
                  <span>Interactive Loan &amp; EMI Calculator</span>
                </h3>
              </div>

              {/* Quick Presets */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs font-bold text-slate-600 mr-1">Presets:</span>
                <button
                  type="button"
                  onClick={() => applyLoanPreset('personal')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    loanType === 'personal'
                      ? 'bg-blue-700 text-white shadow-xs'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  Personal (₹5L)
                </button>
                <button
                  type="button"
                  onClick={() => applyLoanPreset('business')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    loanType === 'business'
                      ? 'bg-blue-700 text-white shadow-xs'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  Business / MSME (₹20L)
                </button>
                <button
                  type="button"
                  onClick={() => applyLoanPreset('home')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    loanType === 'home'
                      ? 'bg-blue-700 text-white shadow-xs'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  Home / LAP (₹50L)
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              {/* Sliders Left */}
              <div className="lg:col-span-7 space-y-6">
                <div>
                  <div className="flex justify-between text-xs font-bold text-slate-700 mb-1.5">
                    <span>Loan Amount Required:</span>
                    <span className="font-mono text-blue-900 font-extrabold text-sm bg-blue-50 px-2 py-0.5 rounded-lg border border-blue-200">
                      ₹{(loanAmount / 100000).toFixed(1)} Lakhs ({loanAmount.toLocaleString('en-IN')})
                    </span>
                  </div>
                  <input
                    type="range"
                    min="100000"
                    max="10000000"
                    step="50000"
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(Number(e.target.value))}
                    className="w-full h-2.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-700"
                  />
                  <div className="flex justify-between text-[11px] text-slate-400 mt-1 font-mono">
                    <span>₹1 Lakh</span>
                    <span>₹50 Lakhs</span>
                    <span>₹1 Crore</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs font-bold text-slate-700 mb-1.5">
                    <span>Interest Rate (% p.a.):</span>
                    <span className="font-mono text-blue-900 font-extrabold text-sm bg-blue-50 px-2 py-0.5 rounded-lg border border-blue-200">
                      {interestRate}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="8"
                    max="22"
                    step="0.25"
                    value={interestRate}
                    onChange={(e) => setInterestRate(Number(e.target.value))}
                    className="w-full h-2.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-700"
                  />
                  <div className="flex justify-between text-[11px] text-slate-400 mt-1 font-mono">
                    <span>8% (Home Loan)</span>
                    <span>12.5% (MSME Loan)</span>
                    <span>22% (Personal)</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs font-bold text-slate-700 mb-1.5">
                    <span>Repayment Tenure:</span>
                    <span className="font-mono text-blue-900 font-extrabold text-sm bg-blue-50 px-2 py-0.5 rounded-lg border border-blue-200">
                      {tenureYears} Years ({totalMonths} Months)
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="20"
                    step="1"
                    value={tenureYears}
                    onChange={(e) => setTenureYears(Number(e.target.value))}
                    className="w-full h-2.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-700"
                  />
                  <div className="flex justify-between text-[11px] text-slate-400 mt-1 font-mono">
                    <span>1 Year</span>
                    <span>5 Years</span>
                    <span>20 Years</span>
                  </div>
                </div>
              </div>

              {/* Output Summary Right */}
              <div className="lg:col-span-5 bg-gradient-to-b from-blue-50/80 to-slate-50 p-6 rounded-2xl border-2 border-blue-200 space-y-4">
                <div className="text-center pb-3 border-b border-blue-200">
                  <span className="text-xs font-black uppercase tracking-wider text-slate-500 block">
                    Calculated Monthly EMI
                  </span>
                  <div className="text-3xl sm:text-4xl font-black text-[#0c2e6b] font-mono mt-1">
                    ₹{emi.toLocaleString('en-IN')} <span className="text-xs font-normal text-slate-500">/ mo</span>
                  </div>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="flex justify-between text-slate-600">
                    <span>Principal Amount:</span>
                    <span className="font-bold text-slate-800 font-mono">₹{loanAmount.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Total Interest:</span>
                    <span className="font-bold text-slate-800 font-mono">₹{totalInterest.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Total Repayment:</span>
                    <span className="font-bold text-slate-800 font-mono">₹{totalPayment.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-emerald-700 font-bold pt-1.5 border-t border-blue-200">
                    <span>Refer &amp; Earn Payout (~2.2%):</span>
                    <span className="font-mono text-sm font-black">+₹{partnerCommission.toLocaleString('en-IN')}</span>
                  </div>
                </div>

                {/* Action Buttons: Submit + WhatsApp */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => onOpenInquiry(`Loan Application: ₹${(loanAmount / 100000).toFixed(1)} Lakhs (${tenureYears} Years @ ${interestRate}%)`)}
                    className="flex-1 py-3.5 rounded-xl bg-gradient-to-r from-blue-700 to-blue-900 hover:from-blue-800 hover:to-blue-950 text-white font-black text-xs uppercase tracking-wider transition-all shadow-md shadow-blue-800/25 flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <Send className="w-4 h-4" />
                    <span>Apply for this Loan</span>
                  </motion.button>

                  <a
                    href={`https://wa.me/91${OFFICIAL_LINKS.phone}?text=${encodeURIComponent(`Hello Fi Pay Bills, I want to apply for a Loan of ₹${(loanAmount / 100000).toFixed(1)} Lakhs for ${tenureYears} years with estimated EMI ₹${emi}. Please advise on partner banks.`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="py-3.5 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-sm"
                    title="Apply via WhatsApp"
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Animated Product Cards Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedCategory}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {currentProducts.map((prod) => {
              const isGreen = prod.colorTheme === 'green' || prod.isInsurance;
              return (
                <motion.div
                  key={prod.id}
                  whileHover={{ y: -3 }}
                  className={`p-6 rounded-3xl bg-white border-2 transition-all duration-200 flex flex-col justify-between shadow-xs hover:shadow-lg ${
                    isGreen 
                      ? 'border-emerald-200 hover:border-emerald-400' 
                      : 'border-slate-200 hover:border-blue-400'
                  }`}
                >
                  <div className="space-y-3.5">
                    {/* Top Tag & Settlement */}
                    <div className="flex items-center justify-between gap-2">
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                        isGreen 
                          ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
                          : 'bg-blue-50 text-blue-800 border border-blue-200'
                      }`}>
                        {prod.shortTag}
                      </span>
                      {prod.settlementTime && (
                        <span className="text-[11px] font-bold text-slate-500 flex items-center gap-1">
                          <Zap className="w-3 h-3 text-amber-500" />
                          <span>{prod.settlementTime}</span>
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <h3 className={`text-lg font-black tracking-tight ${
                      isGreen ? 'text-emerald-950' : 'text-[#0c2e6b]'
                    }`}>
                      {prod.title}
                    </h3>

                    {/* Tagline */}
                    <p className="text-xs text-slate-600 leading-relaxed min-h-[36px]">
                      {prod.tagline}
                    </p>

                    {/* Aggregators / Integrations Badges */}
                    {prod.partnerIntegrations && prod.partnerIntegrations.length > 0 && (
                      <div className="pt-1">
                        <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block mb-1.5">
                          Integrated Networks:
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {prod.partnerIntegrations.slice(0, 4).map((p, idx) => (
                            <span 
                              key={idx} 
                              className="px-2 py-0.5 bg-slate-100 text-slate-700 text-[11px] font-semibold rounded-md"
                            >
                              {p}
                            </span>
                          ))}
                          {prod.partnerIntegrations.length > 4 && (
                            <span className="px-1.5 py-0.5 text-slate-400 text-[11px]">
                              +{prod.partnerIntegrations.length - 4} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions: View More + WhatsApp */}
                  <div className="pt-6 flex items-center gap-2">
                    <button
                      onClick={() => onSelectProduct(prod)}
                      className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-xs ${
                        isGreen
                          ? 'bg-emerald-700 hover:bg-emerald-800 text-white'
                          : 'bg-blue-700 hover:bg-blue-800 text-white'
                      }`}
                    >
                      <Eye className="w-3.5 h-3.5" />
                      <span>View More</span>
                    </button>

                    <a
                      href={`https://wa.me/91${OFFICIAL_LINKS.phone}?text=${encodeURIComponent(`Hello Fi Pay Bills, I want to inquire about "${prod.title}" (${prod.shortTag}). Please share onboarding and commercial details.`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="py-2.5 px-3 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold text-xs flex items-center justify-center gap-1 transition-colors cursor-pointer shadow-2xs"
                      title="Chat about this service on WhatsApp"
                    >
                      <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
                      <span className="hidden sm:inline">WhatsApp</span>
                    </a>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
